import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from "react";
import { nanoid } from "nanoid";
import "highcharts/modules/map";
import merge from "@tinkoff/utils/object/mergeDeep";
import { useSignal } from "@preact/signals";
import BaseChart from "../BaseChart";
import MapTooltip from "../../Tooltip/MapTooltip";
import Loader from "../../Loader";
import style from "./style.module.scss";
/**
 * The WorldMap component is mostly a wrapper around the Chart component, with
 * some chart option overrides. It also loads the world map GeoJSON, which is
 * fairly large. Keeping WorldMap in a separate component ensures that the GeoJSON
 * is not loaded unnecessarily.
 */
export default function WorldMap(props) {
    const baseMap = useSignal(undefined);
    useEffect(() => {
        import("@highcharts/map-collection/custom/world.geo.json").then((module) => (baseMap.value = module.default));
    }, [baseMap]);
    if (baseMap.value === undefined) {
        return _jsx(Loader, { height: props.loaderHeight });
    }
    return (_jsx("div", { className: style["world-map"], children: _jsx(BaseChart, Object.assign({ extending: true, tooltip: MapTooltip }, props, { type: "map", finalChartOptions: (options) => applyMapOptions(baseMap.value, options) })) }));
}
function applyMapOptions(baseMap, options) {
    const chartOptions = merge(options, {
        chart: {
            type: "map",
            map: baseMap,
            height: null,
            spacing: [0, 0, 0, 0],
        },
        xAxis: {
            visible: false,
        },
        yAxis: {
            visible: false,
        },
        plotOptions: {
            series: {
                maxSize: 40,
                minSize: 4,
            },
        },
        legend: {
            enabled: false,
        },
        mapNavigation: {
            enabled: true,
            enableMouseWheelZoom: false,
            buttonOptions: {
                style: {
                    fontWeight: "800",
                    color: "var(--high-emphasis)",
                    fontFamily: "Gotham",
                },
                theme: {
                    fill: "var(--button-bg-color)",
                    "stroke-width": 1,
                    stroke: "var(--background-color)",
                    r: 4,
                    states: {
                        hover: {
                            fill: "var(--button-bg-hover-color)",
                            style: {
                                color: "var(--inverse-high-emphasis)",
                            },
                        },
                        select: {
                            fill: "var(--button-bg-hover-color)",
                            style: {
                                color: "var(--inverse-high-emphasis)",
                            },
                        },
                    },
                },
                x: 1,
                verticalAlign: "bottom",
            },
        },
    });
    // Convert all of the series to "mapbubble" type and generate a unique ID to force Highcharts
    // to recalculate the bubble size extremes.
    const originalSeries = chartOptions.series || [];
    const transformedSeries = originalSeries.map((series) => (Object.assign(Object.assign({}, series), { type: "mapbubble", id: nanoid() })));
    const baseMapSeries = {
        type: "map",
        borderColor: "var(--base-map-color)",
        borderWidth: 0.5,
        nullColor: "var(--base-map-color)",
        showInLegend: false,
        cursor: "pointer",
        states: {
            inactive: {
                opacity: 1,
            },
        },
    };
    // Inject the base map as the first series
    chartOptions.series = [baseMapSeries, ...transformedSeries];
    return chartOptions;
}
